/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import PropTypes from "prop-types";

import iconEditorReducer from "js/reducers/icon-editor";
import { iconEditorInitialState } from "js/reducers/icon-editor/state";

import {
    persistUpdatedRecentColorsPalettes,
    fetchToken,
    persistUpdatedShape,
    getPreviewImage,
    getPersistedRecentColorsPalette,
    getPersistedShape,
    iconIsEdited
} from "./helpers";

import Loading from "icons/IconDetailPage/Loading";

import { ICON_EDITOR_PREVIEW_TOKEN } from "core/localStorageKeys";
import { getLocalStorage, setLocalStorage } from "js/helpers/functions";

const IconEditorStateContext = createContext();
const IconEditorDispatchContext = createContext();
const IconEditorVisibilityContext = createContext();

const IconEditorProvider = ({ iconId, children }) => {
    const [state, dispatch] = React.useReducer(
        iconEditorReducer,
        iconEditorInitialState
    );

    const [showControls, setShowControls] = useState(true);
    const [showAttribution, setShowAttribution] = useState(false);
    const resetEditor = useCallback(async () => {
        return dispatch({ type: "RESET_EDITOR" });
    }, []);
    const visibilitySettings = {
        showControls,
        setShowControls,
        showAttribution,
        setShowAttribution,
        resetEditor
    };

    useEffect(() => {
        const persistedToken = getLocalStorage(ICON_EDITOR_PREVIEW_TOKEN);

        const persistedShape = getPersistedShape();

        const {
            persistedIconsColors,
            persistedBackgroundColors
        } = getPersistedRecentColorsPalette();

        dispatch({
            type: "INITIALIZE_EDITOR",
            payload: {
                iconId,
                persistedToken,
                persistedIconsColors,
                persistedBackgroundColors,
                persistedShape
            }
        });
    }, []);

    // If react-router changes the icon ID in the URL, update page
    useEffect(() => {
        if (
            state.currentIconId &&
            String(iconId) !== String(state.currentIconId)
        ) {
            window.scroll(0, 0); // if next icon is cached, we need to manually scroll to top
            dispatch({
                type: "UPDATE_ICON_ID",
                iconId
            });
        }
    }, [iconId, state.currentIconId]);

    useEffect(() => {
        if (state.currentIconId) {
            dispatch({
                type: "FETCH_PREVIEW_IMAGE"
            });
        }
    }, [state.currentIconId, state.editCount]);

    useEffect(() => {
        if (
            !!state.token &&
            state.token !== getLocalStorage(ICON_EDITOR_PREVIEW_TOKEN)
        ) {
            setLocalStorage(ICON_EDITOR_PREVIEW_TOKEN, state.token);
        }
    }, [state.token]);

    useEffect(() => {
        if (state.fetchingPreviewImage) {
            getPreviewImage({ state, dispatch });
        }
    }, [state.fetchingPreviewImage]);

    useEffect(() => {
        if (state.fetchingToken) {
            const getToken = async () => {
                const response = await fetchToken();
                if (!response.ok) {
                    dispatch({
                        type: "FETCH_PREVIEW_IMAGE_TOKEN_FAILURE"
                    });
                }

                const { token } = await response.json();
                dispatch({
                    type: "FETCH_PREVIEW_IMAGE_TOKEN_SUCCESS",
                    token
                });
            };

            getToken();
        }
    }, [state.fetchingToken]);

    const { backgroundColors, iconColors } = state;
    const recentBackgroundColors = backgroundColors.recent;
    const recentIconColors = iconColors.recent;
    const firstRecentBackgroundColor = recentBackgroundColors[0];
    const firstRecentIconColor = recentIconColors[0];

    useEffect(() => {
        persistUpdatedRecentColorsPalettes({
            updatedRecentIconColors: recentIconColors,
            updatedRecentBackgroundColors: recentBackgroundColors
        });
    }, [firstRecentBackgroundColor, firstRecentIconColor]);

    useEffect(() => {
        persistUpdatedShape({ shape: state.shape });
    }, [state.shape]);

    // On _initial_ render, wait until state fully loads prior to rendering children.
    // This is an optimization to reduce extraneous renders of expensive components.
    if (state.loadingInitialPreview) {
        return <Loading />;
    }

    return (
        <IconEditorStateContext.Provider value={state}>
            <IconEditorDispatchContext.Provider value={dispatch}>
                <IconEditorVisibilityContext.Provider
                    value={visibilitySettings}
                >
                    {children}
                </IconEditorVisibilityContext.Provider>
            </IconEditorDispatchContext.Provider>
        </IconEditorStateContext.Provider>
    );
};

IconEditorProvider.propTypes = {
    iconId: PropTypes.string.isRequired
};

export { IconEditorProvider };

export const useIconEditorState = () => {
    const context = useContext(IconEditorStateContext);

    if (context === undefined) {
        throw new Error(
            "useIconEditorState must be used within an IconEditorProvider"
        );
    }

    return context;
};

export const useIconEditorDispatch = () => {
    const context = useContext(IconEditorDispatchContext);

    if (context === undefined) {
        throw new Error(
            "useIconEditorDispatch must be used within an IconEditorProvider"
        );
    }

    return context;
};

export const useIconEditorVisibility = () => {
    const context = useContext(IconEditorVisibilityContext);

    if (context === undefined) {
        throw new Error(
            "useIconEditorVisibility must be used within an IconEditorProvider"
        );
    }

    return context;
};

export const useIconIsEdited = ({ iconId }) => {
    const state = useIconEditorState();
    return iconIsEdited({ state, iconId });
};
